/**
 * Dépenses de branding (MC3) — saisies sur la page Frais.
 *
 * Même forme qu'une charge fixe, sans catégorie : libellé, montant HT par
 * échéance, fréquence, période. Chaque dépense est lissée au prorata des jours
 * de la période affichée, et MC3 = MC2 − branding sur le Résumé et la page
 * Shopify. La liste peut rester vide : la MC3 vaut alors la MC2.
 */

import type { BrandingExpense, BrandingExpenseInput } from '@shared/types/branding';
import {
  CHARGE_AMOUNT_MAX,
  CHARGE_FREQUENCIES,
  CHARGE_FREQUENCY_LABELS,
  CHARGE_LABEL_MAX_LENGTH,
  CHARGE_NOTE_MAX_LENGTH,
  type ChargeFrequency,
} from '@shared/types/charges';
import { useEffect, useState, type FormEvent } from 'react';

import { useBrandingMutations, useBrandingStats } from '../hooks/useBranding';
import { useAuth } from '../hooks/useAuth';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { todayInParis } from '../lib/dates';
import { formatDayLong, formatMoney } from '../lib/format';
import { Card } from './Card';

interface Draft {
  label: string;
  amount: string;
  frequency: ChargeFrequency;
  startDate: string;
  endDate: string;
  note: string;
}

const emptyDraft = (): Draft => ({ label: '', amount: '', frequency: 'once', startDate: todayInParis(), endDate: '', note: '' });
const toDraft = (expense: BrandingExpense): Draft => ({
  label: expense.label,
  amount: String(expense.amount).replace('.', ','),
  frequency: expense.frequency,
  startDate: expense.startDate,
  endDate: expense.endDate ?? '',
  note: expense.note ?? '',
});
const parseAmount = (raw: string): number | null => {
  const cleaned = raw.replace(/\s/g, '').replace('€', '').replace(',', '.');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};
const money = (value: number) => formatMoney(value, 'EUR', true);

export function BrandingEditor() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const query = useBrandingStats({ from: period.from, to: period.to, token, onUnauthorized: signOut });
  const mutations = useBrandingMutations();
  const [editing, setEditing] = useState<BrandingExpense | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(editing ? toDraft(editing) : emptyDraft());
    setError(null);
  }, [editing]);

  const set = <K extends keyof Draft>(key: K) => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [key]: event.target.value as Draft[K] }));
  const once = draft.frequency === 'once';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const amount = parseAmount(draft.amount);
    if (amount === null || amount < 0 || amount > CHARGE_AMOUNT_MAX) return setError('Montant : un nombre positif.');
    if (!once && draft.endDate && draft.endDate < draft.startDate) return setError('La date de fin précède la date de début.');
    setError(null);
    const input: BrandingExpenseInput = {
      label: draft.label,
      amount,
      frequency: draft.frequency,
      startDate: draft.startDate,
      endDate: once ? null : draft.endDate || null,
      note: draft.note || null,
    };
    try {
      if (editing) {
        await mutations.update(editing.id, input);
        setEditing(null);
      } else {
        await mutations.create(input);
        setDraft((current) => ({ ...current, label: '', amount: '', note: '' }));
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Enregistrement impossible.');
    }
  };

  const expenses = query.data?.expenses ?? [];
  const total = query.data?.comparison.current.total ?? 0;

  return (
    <Card
      title="Branding (MC3)"
      subtitle={`Dépenses de marque hors campagnes pub : influence, sponsoring, contenu, salons… Montant HT par échéance, lissé au prorata des jours. MC3 = MC2 − branding. Sur la période affichée : ${money(total)}.`}
      className="marker-form-card"
    >
      {query.isError && (
        <p className="notice notice--error" role="alert">
          Branding illisible : {query.error.message}
        </p>
      )}
      <form className="marker-form" onSubmit={(event) => void handleSubmit(event)}>
        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="branding-label">Libellé</label>
          <input id="branding-label" type="text" required maxLength={CHARGE_LABEL_MAX_LENGTH} value={draft.label} onChange={set('label')} placeholder="ex. Partenariat influence, Salon Musicora" />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="branding-amount">Montant HT (€)</label>
          <input id="branding-amount" type="text" inputMode="decimal" required value={draft.amount} onChange={set('amount')} style={{ width: 110 }} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="branding-frequency">Fréquence</label>
          <select id="branding-frequency" value={draft.frequency} onChange={set('frequency')}>
            {CHARGE_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {CHARGE_FREQUENCY_LABELS[frequency]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="branding-start">{once ? 'Date' : 'Début'}</label>
          <input id="branding-start" type="date" required value={draft.startDate} onChange={set('startDate')} />
        </div>
        {!once && (
          <div className="field">
            <label className="field__label" htmlFor="branding-end">Fin (facultative)</label>
            <input id="branding-end" type="date" value={draft.endDate} onChange={set('endDate')} />
          </div>
        )}
        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="branding-note">Note</label>
          <input id="branding-note" type="text" maxLength={CHARGE_NOTE_MAX_LENGTH} value={draft.note} onChange={set('note')} />
        </div>
        <div className="marker-form__actions">
          <button type="submit" className="btn btn--primary" disabled={mutations.isSaving}>
            {mutations.isSaving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter'}
          </button>
          {editing && (
            <button type="button" className="btn" onClick={() => setEditing(null)} disabled={mutations.isSaving}>
              Annuler
            </button>
          )}
        </div>
        {error && (
          <p className="login__error marker-form__error" role="alert">
            {error}
          </p>
        )}
      </form>

      {expenses.length === 0 ? (
        <p className="empty">Aucune dépense de branding saisie : la MC3 vaut la MC2.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Libellé</th>
                <th className="num">Montant HT</th>
                <th>Fréquence</th>
                <th>Du</th>
                <th>Au</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id}>
                  <td className="wrap">
                    {expense.label}
                    {expense.note && <span className="marker-note">{expense.note}</span>}
                  </td>
                  <td className="num">{money(expense.amount)}</td>
                  <td>{CHARGE_FREQUENCY_LABELS[expense.frequency]}</td>
                  <td>{formatDayLong(expense.startDate)}</td>
                  <td>{expense.frequency === 'once' ? '—' : expense.endDate ? formatDayLong(expense.endDate) : 'en cours'}</td>
                  <td>
                    <button type="button" className="btn btn--small" disabled={mutations.isSaving} onClick={() => setEditing(expense)}>
                      Modifier
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn--small"
                      disabled={mutations.isSaving}
                      onClick={() => {
                        if (!window.confirm(`Supprimer « ${expense.label} » (${money(expense.amount)}) ?`)) return;
                        if (editing?.id === expense.id) setEditing(null);
                        void mutations.remove(expense.id);
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
